import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CrownIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon, ShieldCheckIcon, ClockIcon, UsersIcon, StarIcon, BriefcaseIcon, EyeOffIcon, TrendingUpIcon, ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { ContactForm } from '../../components/ContactForm';
import { HeroLeadForm } from '../../components/HeroLeadForm';
import { ClientLogosStrip } from '../../components/ClientLogosStrip';
import { TestimonialsSection } from '../../components/TestimonialsSection';
import { FaqSection } from '../../components/FaqSection';
import { useSEO } from '../../hooks/useSEO';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslations } from '../../i18n/translations';
function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  return <section className="relative pt-14 lg:pt-16 pb-12 lg:pb-14 overflow-hidden bg-navy-900" data-id="element-1009" data-mp="309btzxzhdanm">
      <div className="absolute inset-0" data-id="element-1010" data-mp="34c8gw0w1zsxd">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-500/8 blur-3xl hidden sm:block" data-id="element-1011" data-mp="34db4nzy2xf92" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-cyan-500/6 blur-3xl hidden sm:block" data-id="element-1012" data-mp="2vbad9jpj1nfb" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} data-id="element-1013" data-mp="3j0cu4pcc7c8r" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-1014" data-mp="3998le7d98nmd">
        <motion.nav initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="flex items-center gap-1.5 text-sm text-navy-400 mb-6" data-id="element-1015" data-mp="3e8blddedo3nh">
          <Link to="/" className="hover:text-primary-400 transition-colors" data-id="element-1016" data-mp="fhys1gftzvjh">
            {t.nav.home}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1017" data-mp="aice14s6ib9m" />
          <Link to="/#services" className="hover:text-primary-400 transition-colors" data-id="element-1018" data-mp="ah2esc9lax1e">
            {t.nav.services}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1019" data-mp="1j8szv9gblsh" />
          <span className="text-navy-300" data-id="element-1020" data-mp="1k0c46lv3r1v">{t.executiveSearch.title}</span>
        </motion.nav>

        <div className="max-w-4xl" data-id="element-1021" data-mp="3sv1s4uaveogg">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="flex items-center gap-3 mb-5" data-id="element-1022" data-mp="3nx0cykgkf9a9">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 text-white shadow-xl shadow-primary-500/30" data-id="element-1023" data-mp="3a03k6dgvzhqq">
              <CrownIcon className="w-5 h-5" data-id="element-1024" data-mp="1ud85wnbf8d9b" />
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-500/20 text-primary-300 text-xs font-semibold rounded-full border border-primary-500/20" data-id="element-1025" data-mp="1yhpmyeqeccge">
              {t.executiveSearch.badge}
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.08]" data-id="element-1026" data-mp="16nnklxrsp3wv">
            {t.executiveSearch.h1_1}{' '}
            <span className="text-primary-400" data-id="element-1027" data-mp="rz9ixq647lhw">
              {t.executiveSearch.h1_accent}
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.18
        }} className="mt-4 text-base sm:text-lg text-navy-300 leading-relaxed" data-id="element-1028" data-mp="2ul56stv9rxkd">
            {t.executiveSearch.subtitle}
          </motion.p>
        </div>

        <div className="mt-6 lg:mt-8" data-id="element-1029" data-mp="38dl032ydch83">
          <HeroLeadForm positions={t.executiveSearch.formPositions} source="executive_search_hero" data-id="element-1030" data-mp="38gqqcliqhp5e" />
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-1031" data-mp="3ickaapys8kx7">
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1032" data-mp="1dvnq51c2hzr6">
            <div className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-1033" data-mp="1hvoa11hxr35m">
              {t.executiveSearch.stats.days_val}
            </div>
            <div className="text-xs text-navy-400 mt-1" data-id="element-1034" data-mp="193m80ys7xet8">
              {t.executiveSearch.stats.days_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1035" data-mp="u5bryxsz7q1b">
            <div className="text-2xl sm:text-3xl font-extrabold text-primary-400" data-id="element-1036" data-mp="z7j71w93rm7v">
              {t.executiveSearch.stats.guarantee_val}
            </div>
            <div className="text-xs text-navy-400 mt-1" data-id="element-1037" data-mp="q4rtnqwo2gdl">
              {t.executiveSearch.stats.guarantee_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1038" data-mp="2fkr8nomyv015">
            <div className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-1039" data-mp="2kistof3yd9f9">
              {t.executiveSearch.stats.closed_val}
            </div>
            <div className="text-xs text-navy-400 mt-1" data-id="element-1040" data-mp="2e3aqiqd8vuia">
              {t.executiveSearch.stats.closed_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1041" data-mp="294r3snqtueov">
            <div className="text-2xl sm:text-3xl font-extrabold text-primary-400" data-id="element-1042" data-mp="2466bdahtgzaz">
              {t.executiveSearch.stats.success_val}
            </div>
            <div className="text-xs text-navy-400 mt-1" data-id="element-1043" data-mp="2wtsg4m2s69zf">
              {t.executiveSearch.stats.success_label}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
function WhatWeDoSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-white" ref={ref} data-id="element-1044" data-mp="3fs4pa7tozjv0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1045" data-mp="3fql7gcse10r4">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1046" data-mp="3n53yd4hosko6">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1047" data-mp="9xsqmtb4wmzc">
            {t.executiveSearch.whatWeDo.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1048" data-mp="etrca952vqw2">
            {t.executiveSearch.whatWeDo.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-1049" data-mp="oldwuw3b32bh">
            {t.executiveSearch.whatWeDo.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1050" data-mp="xm3m1gy29ug8">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1051" data-mp="1ixjy00w08izn">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1052" data-mp="107c0byb2qkqu">
              <EyeOffIcon className="w-5 h-5" data-id="element-1053" data-mp="19ygfoz4nw7m7" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1054" data-mp="155na691dcv92">
              {t.executiveSearch.whatWeDo.items[0].title}
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-1055" data-mp="hezxzbkf7jgp">
              {t.executiveSearch.whatWeDo.items[0].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1056" data-mp="2o4l7owyv7z0i">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1057" data-mp="3bufi77vppbhl">
              <UsersIcon className="w-5 h-5" data-id="element-1058" data-mp="2xwr1mc8ogqic" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1059" data-mp="32senahnyb1j3">
              {t.executiveSearch.whatWeDo.items[1].title}
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-1060" data-mp="1mhu5yjhzn320">
              {t.executiveSearch.whatWeDo.items[1].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1061" data-mp="21snawta957mf">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1062" data-mp="1n2pqmofg122i">
              <ShieldCheckIcon className="w-5 h-5" data-id="element-1063" data-mp="1e1hr6316xv6u" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1064" data-mp="1e3vn0e3p3bfg">
              {t.executiveSearch.whatWeDo.items[2].title}
              <span className="ml-2 text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-100 px-2 py-0.5 rounded-full" data-id="element-1065" data-mp="38fiqsq4kem7z">
                {t.executiveSearch.whatWeDo.items[2].badge}
              </span>
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-1066" data-mp="3dlxmev8vtxyq">
              {t.executiveSearch.whatWeDo.items[2].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1067" data-mp="3mlko7i5ix5sb">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1068" data-mp="a1t70o67ybg3">
              <StarIcon className="w-5 h-5" data-id="element-1069" data-mp="2bx3m39cse0jw" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1070" data-mp="evfzmdfdcc5g">
              {t.executiveSearch.whatWeDo.items[3].title}
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-1071" data-mp="jtzlkdtypnxt">
              {t.executiveSearch.whatWeDo.items[3].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="p-7 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1072" data-mp="tsykv5su8785">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1073" data-mp="1hhrjxb4rav6z">
              <BriefcaseIcon className="w-5 h-5" data-id="element-1074" data-mp="1lgqjshbms1rk" />
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1075" data-mp="362oxnceb4k9k">
              {t.executiveSearch.whatWeDo.items[4].title}
            </h3>
            <p className="text-navy-500 text-sm leading-relaxed" data-id="element-1076" data-mp="2x0frsztzhf2i">
              {t.executiveSearch.whatWeDo.items[4].desc}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.35
        }} className="p-7 rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 border border-primary-500" data-id="element-1077" data-mp="2s375qo5psqvz">
            <div className="w-12 h-12 rounded-xl bg-white/20 text-white flex items-center justify-center mb-5" data-id="element-1078" data-mp="ccb6c93uz663">
              <TrendingUpIcon className="w-5 h-5" data-id="element-1079" data-mp="7hot00e7v3jd" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2" data-id="element-1080" data-mp="3o5bcj4yjgbpu">
              {t.executiveSearch.whatWeDo.items[5].title}
            </h3>
            <p className="text-primary-100 text-sm leading-relaxed" data-id="element-1081" data-mp="3e8osgie1doov">
              {t.executiveSearch.whatWeDo.items[5].desc}
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}
function PositionsSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const positions = ['CEO / Генеральний директор', 'COO / Операційний директор', 'CFO / Фінансовий директор', 'CTO / Технічний директор', 'CMO / Директор з маркетингу', 'CHRO / HR директор', 'CSO / Директор з продажів', 'CDO / Директор з цифрової трансформації', 'Board Member / Член ради директорів', 'Country Manager', 'VP of Engineering', 'VP of Product'];
  return <section className="py-20 lg:py-28 bg-navy-50/40" ref={ref} data-id="element-1082" data-mp="2j6og0cw2g2xa">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1083" data-mp="2e9ejyee68t0h">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-1084" data-mp="2e6sc0dndcbhq">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-1085" data-mp="2rzrrifn5i5pa">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1086" data-mp="29a1xhur7pnyf">
              {t.executiveSearch.positions.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-1087" data-mp="24d1nx9v5m32k">
              {t.executiveSearch.positions.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-1088" data-mp="3fpc8wu3nouwg">
              {t.executiveSearch.positions.subtitle}
            </p>
            <div className="mt-8 p-6 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1089" data-mp="2ibiwslqix4yu">
              <div className="flex items-start gap-3" data-id="element-1090" data-mp="2md4huud7opk4">
                <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-1091" data-mp="2mefiilbd1h70" />
                <p className="text-navy-600 text-sm leading-relaxed" dangerouslySetInnerHTML={{
                __html: t.executiveSearch.positions.note.replace('15+ років', '<strong class="text-navy-900">15+ років</strong>').replace('15+ лет', '<strong class="text-navy-900">15+ лет</strong>').replace('15+ years', '<strong class="text-navy-900">15+ years</strong>')
              }} data-id="element-1092" data-mp="2raxofoifflkx" />
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1093" data-mp="13fhm60plojh5">
            {t.executiveSearch.positions.items.map((pos, i) => <motion.div key={pos} initial={{
            opacity: 0,
            y: 12
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.4,
            delay: 0.1 + i * 0.04
          }} className="flex items-center gap-2.5 p-3.5 rounded-xl bg-white border border-navy-100" data-id="element-1094" data-mp="fp596bfhzwjp" data-mpi={i}>
                <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" data-id="element-1095" data-mp="1gn6t6poag7m4" />
                <span className="text-sm font-medium text-navy-700" data-id="element-1096" data-mp="1kl4jjvtsozr3">{pos}</span>
              </motion.div>)}
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.4,
            delay: 0.1 + t.executiveSearch.positions.items.length * 0.04
          }} className="sm:col-span-2 flex items-center gap-2.5 p-3.5 rounded-xl bg-primary-50 border border-primary-100" data-id="element-1097" data-mp="16t7vc2v8z9zz">
              <div className="w-2 h-2 rounded-full bg-primary-400 flex-shrink-0" data-id="element-1098" data-mp="9bkq7quhi271" />
              <span className="text-sm font-medium text-primary-700 italic" data-id="element-1099" data-mp="4chh9vbdvbts">
                {t.executiveSearch.positions.other}
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function ProcessSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const steps = t.executiveSearch.process.steps.map((step, i) => ({
    num: `0${i + 1}`,
    title: step.title,
    desc: step.desc,
    side: i % 2 === 0 ? 'left' : 'right'
  }));
  return <section className="py-20 lg:py-28 bg-white" id="process" ref={ref} data-id="element-1100" data-mp="1ayk57xbec5h9">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1101" data-mp="1awqyki0s9l99">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-16" data-id="element-1102" data-mp="161lcnj7ds6l6">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1103" data-mp="1nhjtfkdn392e">
            {t.executiveSearch.process.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1104" data-mp="zpwappqj74b7">
            {t.executiveSearch.process.title}
          </h2>
        </motion.div>

        {/* Desktop timeline */}
        <div className="hidden md:block relative" data-id="element-1105" data-mp="14qtu25na24ea">
          {/* Center line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary-200 via-primary-300 to-primary-100 -translate-x-1/2" data-id="element-1106" data-mp="h1rcexsn8bjs" />

          <div className="space-y-12" data-id="element-1107" data-mp="c1vad43iau6v">
            {steps.map((step, i) => <motion.div key={step.num} initial={{
            opacity: 0,
            y: 24
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className={`relative flex items-center gap-8 ${step.side === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-1108" data-mp="2nprnoihnko0g" data-mpi={i}>
                {/* Content card */}
                <div className="w-[calc(50%-2.5rem)]" data-id="element-1109" data-mp="2xi5uhpnl8kth">
                  <div className={`p-6 rounded-2xl bg-navy-50/70 border border-navy-100 ${step.side === 'right' ? 'text-right' : ''}`} data-id="element-1110" data-mp="32duzi8idmtx6">
                    <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1111" data-mp="1r0adbq7q6dsf">
                      {step.title}
                    </h3>
                    <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1112" data-mp="3snlod4mpfrpn">
                      {step.desc}
                    </p>
                  </div>
                </div>

                {/* Center node */}
                <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center w-14 h-14 rounded-full bg-white border-2 border-primary-300 shadow-lg shadow-primary-100/50 z-10" data-id="element-1113" data-mp="3ixai3bfjt6fl">
                  <span className="text-sm font-extrabold text-primary-600" data-id="element-1114" data-mp="39zqfnz5y943e">
                    {step.num}
                  </span>
                </div>

                {/* Empty side */}
                <div className="w-[calc(50%-2.5rem)]" data-id="element-1115" data-mp="300rd8ybs24ou" />
              </motion.div>)}
          </div>
        </div>

        {/* Mobile timeline */}
        <div className="md:hidden relative pl-10" data-id="element-1116" data-mp="y9ec8kcxkgy2">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-primary-200 via-primary-300 to-primary-100" data-id="element-1117" data-mp="y8364dw0ix3s" />
          <div className="space-y-8" data-id="element-1118" data-mp="2c4xi537fabvt">
            {steps.map((step, i) => <motion.div key={step.num} initial={{
            opacity: 0,
            x: -16
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.4,
            delay: i * 0.08
          }} className="relative" data-id="element-1119" data-mp="2c2tgkrwzxoyp" data-mpi={i}>
                <div className="absolute -left-10 top-4 flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-primary-300 shadow-md z-10" data-id="element-1120" data-mp="226881wlrpvq4">
                  <span className="text-[10px] font-extrabold text-primary-600" data-id="element-1121" data-mp="273gkdgk8j5jg">
                    {step.num}
                  </span>
                </div>
                <div className="p-5 rounded-2xl bg-navy-50/70 border border-navy-100" data-id="element-1122" data-mp="3ijoovnj88qxr">
                  <h3 className="text-base font-bold text-navy-900 mb-1.5" data-id="element-1123" data-mp="3h4vrslp8d4s5">
                    {step.title}
                  </h3>
                  <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1124" data-mp="3c6kf837jwg68">
                    {step.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}
function CtaBanner() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5
  }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1125" data-mp="dm2fec86ulzk">
      <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 relative overflow-hidden" data-id="element-1126" data-mp="hmmg1prz07qg">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary-500/10 blur-3xl hidden sm:block" data-id="element-1127" data-mp="16a3ugodouyrb" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-primary-400/10 blur-3xl hidden sm:block" data-id="element-1128" data-mp="sakljtd1zjdy" />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10" data-id="element-1129" data-mp="wa34wkp87meo">
          <div className="flex-1" data-id="element-1130" data-mp="ij7uv52zqz9i">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-1131" data-mp="ii6imxa8o6q7">
              {t.executiveSearch.ctaBanner.title}
            </h3>
            <p className="mt-2 text-navy-300 leading-relaxed" data-id="element-1132" data-mp="8mv5ukie59l3">
              {t.executiveSearch.ctaBanner.subtitle}
            </p>
          </div>
          <a href="#contact" className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-navy-900 bg-white rounded-xl hover:bg-navy-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-1133" data-mp="dik3giauoger">
            {t.executiveSearch.ctaBanner.button}
            <ArrowRightIcon className="w-4 h-4" data-id="element-1134" data-mp="21inwfbte5ljf" />
          </a>
        </div>
      </div>
    </motion.div>;
}
const seoData = {
  UA: {
    title: 'Executive Search — Підбір керівників вищої ланки | ESCA',
    description: 'Професійний підбір топ-менеджерів та керівників вищої ланки. CEO, CFO, CTO, COO — знаходимо лідерів для вашого бізнесу. 20+ років досвіду.',
    keywords: 'executive search, підбір керівників, хедхантинг, топ-менеджмент, CEO, CFO, CTO, ESCA'
  },
  RU: {
    title: 'Executive Search — Подбор руководителей высшего звена | ESCA',
    description: 'Профессиональный подбор топ-менеджеров и руководителей высшего звена. CEO, CFO, CTO, COO — находим лидеров для вашего бизнеса. 20+ лет опыта.',
    keywords: 'executive search, подбор руководителей, хедхантинг, топ-менеджмент, CEO, CFO, CTO, ESCA'
  },
  EN: {
    title: 'Executive Search — C-Level & Senior Leadership Recruitment | ESCA',
    description: 'Professional executive search for C-level and senior leadership positions. CEO, CFO, CTO, COO — we find leaders for your business. 20+ years of experience.',
    keywords: 'executive search, C-level recruitment, headhunting, senior leadership, CEO, CFO, CTO, ESCA'
  }
};
export function ExecutiveSearchPage() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const seo = seoData[lang];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  return <div className="min-h-screen w-full bg-white overflow-x-hidden" data-id="element-1135" data-mp="32otkepkeue9">
      <Header data-id="element-1136" data-mp="80gkp6idyche" />
      <main className="pt-16 lg:pt-20" data-id="element-1137" data-mp="80qe742n31ru">
        <HeroSection data-id="element-1138" data-mp="c2247apfdp0k" />
        <ClientLogosStrip data-id="element-1139" data-mp="c0iopmblfhzu" />
        <WhatWeDoSection data-id="element-1140" data-mp="c0qzn8o5od8e" />
        <PositionsSection data-id="element-1141" data-mp="29rbiu502enra" />
        <ProcessSection data-id="element-1142" data-mp="29s3efftnwlhk" />
        <TestimonialsSection data-id="element-1143" data-mp="29smuss3nz1th" />
        <FaqSection items={t.executiveSearch.faqItems} data-id="element-1144" data-mp="29to9r7nsw8gh" />
        <CtaBanner data-id="element-1145" data-mp="2dtyhopv5l8xz" />
        <div id="contact" data-id="element-1146" data-mp="2dt6ua2bpmhis">
          <ContactForm data-id="element-1147" data-mp="2duq8lndt2ihx" />
        </div>
      </main>
      <Footer data-id="element-1148" data-mp="1uvdehn1r0yje" />
    </div>;
}